/**
 * MessagerieInternePage — L13.18
 *
 * Messagerie interne mairie : les agents / managers / admin d'un même tenant
 * s'échangent des messages. Distincte de l'inbox citoyenne GRC.
 *
 * Page autonome à vues internes :
 *   - « Reçus »   : les messages reçus (pastille = non lus)
 *   - « Envoyés » : les messages envoyés
 *   - composer    : carnet d'adresses (collègues) + objet + corps
 *   - détail      : un message ; l'ouvrir le marque lu côté reçus
 */

import { useEffect, useState, useCallback } from 'react';
import {
  api,
  ApiException,
  type CarnetEntry,
  type InboxMessage,
  type SentMessage,
  type MessageDetail,
} from '@/lib/api';
import { useToast } from '@/components/ui/Toast';
import {
  Card, Button, Badge, Alert,
} from '@/components/ui';
import { AppHeader } from '@/components/AppHeader';
import { MessagerieGroupesPanel } from '@/components/messagerie/MessagerieGroupesPanel';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import {
  ArrowLeft, Inbox, Send, Plus, Loader2, AlertCircle,
  ChevronRight, Check, Users, Reply, Forward,
  FileText, Archive, ArchiveRestore, CheckCircle2, Trash2, Megaphone,
} from 'lucide-react';

type View = 'inbox' | 'sent' | 'brouillons' | 'groupes' | 'compose' | 'detail';

const ROLE_LABEL: Record<string, string> = {
  super_admin: 'Admin technique',
  admin: 'Mairie',
  manager: 'Chef de service',
  agent: 'Agent',
};

/** Temps relatif court. */
function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.round(diff / 60000);
  if (min < 1) return "à l'instant";
  if (min < 60) return `il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `il y a ${h} h`;
  const j = Math.floor(h / 24);
  if (j < 7) return `il y a ${j} j`;
  return new Date(iso).toLocaleDateString('fr-FR');
}

/** Retire un préfixe « Re: » / « Tr: » existant pour ne pas les empiler. */
function stripObjetPrefix(s: string): string {
  return s.replace(/^\s*(re|tr)\s*:\s*/i, '').trim();
}

/** Badge d'un message rattaché à une alerte/crise (mini-lot alerte, Tranche 1). */
function AlerteBadge({ alerte }: { alerte?: { titre: string; niveau: string } | null }) {
  if (!alerte) return null;
  const crise = alerte.niveau === 'crise';
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-2xs font-semibold ${
        crise ? 'bg-urgent/15 text-urgent' : 'bg-warning/15 text-warning'
      }`}
      title={`Rattaché à ${crise ? 'la crise' : "l'alerte"} : ${alerte.titre}`}
    >
      {crise ? '🔴 Crise' : '🟠 Alerte'} · {alerte.titre}
    </span>
  );
}

export function MessagerieInternePage() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Le bouton « Diffusion » ne s'affiche qu'à l'encadrement. La portée réelle
  // (son secteur, ou toute la mairie) est tranchée par le serveur — ici on
  // n'écarte que ceux qui n'ont rien à y faire.
  const peutDiffuser =
    user?.role === 'manager' || user?.role === 'admin' || user?.role === 'super_admin';

  const [view, setView] = useState<View>('inbox');
  const [inbox, setInbox] = useState<InboxMessage[]>([]);
  const [sent, setSent] = useState<SentMessage[]>([]);

  // Mig 235 — filtres, archives séparées et pagination réglable.
  // `statut` ne s'applique qu'à la boîte de réception : il décrit MON
  // traitement, pas celui du message.
  const [statutFiltre, setStatutFiltre] = useState<'tous' | 'en_cours' | 'traite'>('tous');
  const [archives, setArchives] = useState(false);
  const [pageSize, setPageSize] = useState(25);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [brouillonId, setBrouillonId] = useState<string | null>(null);
  const [unread, setUnread] = useState(0);
  const [detail, setDetail] = useState<MessageDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Composer
  const [carnet, setCarnet] = useState<CarnetEntry[]>([]);
  const [recipients, setRecipients] = useState<string[]>([]);
  const [objet, setObjet] = useState('');
  const [corps, setCorps] = useState('');
  const [sending, setSending] = useState(false);
  const [composeError, setComposeError] = useState<string | null>(null);
  // Rattachement optionnel à une alerte/crise (mini-lot alerte, Tranche 1).
  const [alertesRattachables, setAlertesRattachables] = useState<{ id: string; titre: string; niveau: string }[]>([]);
  const [alerteId, setAlerteId] = useState<string>('');

  const loadInbox = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.messagerie.inbox({
        statut: statutFiltre === 'tous' ? undefined : statutFiltre,
        archives,
        limit: pageSize,
        offset,
      });
      setInbox(res.items);
      setTotal(res.total);
      const u = await api.messagerie.unreadCount();
      setUnread(u.count);
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [statutFiltre, archives, pageSize, offset]);

  const loadSent = useCallback(async (statut: 'brouillon' | 'envoye' = 'envoye') => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.messagerie.sent({
        statut,
        archives: statut === 'envoye' ? archives : false,
        limit: pageSize,
        offset,
      });
      setSent(res.items);
      setTotal(res.total);
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [archives, pageSize, offset]);

  useEffect(() => {
    void loadInbox();
  }, [loadInbox]);

  // Changer de boîte remet la pagination à zéro : rester page 3 en passant des
  // reçus aux brouillons afficherait une liste vide sans raison apparente.
  function goInbox() {
    setView('inbox'); setOffset(0); setArchives(false);
  }

  function goSent() {
    setView('sent'); setOffset(0); setStatutFiltre('tous');
  }

  function goBrouillons() {
    setView('brouillons'); setOffset(0); setArchives(false); setStatutFiltre('tous');
  }

  function goArchives() {
    setView('inbox'); setOffset(0); setArchives(true); setStatutFiltre('tous');
  }

  // Un seul effet pilote les trois boîtes : les dépendances des `useCallback`
  // portent déjà filtres et pagination.
  useEffect(() => {
    if (view === 'inbox') void loadInbox();
    else if (view === 'sent') void loadSent('envoye');
    else if (view === 'brouillons') void loadSent('brouillon');
  }, [view, loadInbox, loadSent]);

  async function basculerTraite(id: string, traite: boolean) {
    try {
      await api.messagerie.traite(id, traite);
      await loadInbox();
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Action impossible');
    }
  }

  async function basculerArchive(id: string, archive: boolean) {
    try {
      await api.messagerie.archive(id, archive);
      if (view === 'inbox') await loadInbox(); else await loadSent('envoye');
      toast.success(archive ? 'Message archivé.' : 'Message désarchivé.');
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Action impossible');
    }
  }

  async function supprimerBrouillon(id: string) {
    try {
      await api.messagerie.brouillons.supprimer(id);
      await loadSent('brouillon');
      toast.success('Brouillon supprimé.');
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Suppression impossible');
    }
  }

  async function openCompose(prefill?: {
    recipients?: string[];
    objet?: string;
    corps?: string;
  }) {
    setView('compose');
    setComposeError(null);
    setRecipients(prefill?.recipients ?? []);
    setObjet(prefill?.objet ?? '');
    setCorps(prefill?.corps ?? '');
    setAlerteId('');
    // Alertes rattachables (alerte/crise) pour le sélecteur — best-effort.
    try {
      const ra = await api.grc.alertes.rattachables();
      setAlertesRattachables(ra.alertes);
    } catch { setAlertesRattachables([]); }
    if (carnet.length === 0) {
      try {
        const res = await api.messagerie.carnet();
        setCarnet(res.items);
      } catch {
        setComposeError('Carnet d\u2019adresses indisponible.');
      }
    }
  }

  /** Répondre : compose préremplie vers l'expéditeur du message ouvert. */
  function replyTo(d: MessageDetail) {
    void openCompose({
      recipients: [d.expediteurUserId],
      objet: `Re: ${stripObjetPrefix(d.objet)}`,
    });
  }

  /** Transférer : compose avec l'objet et le message d'origine cités. */
  function forwardMsg(d: MessageDetail) {
    const quote =
      '\n\n———— Message transféré ————\n' +
      `De : ${d.expediteurNom}\nObjet : ${d.objet}\n\n${d.corps}`;
    void openCompose({ objet: `Tr: ${stripObjetPrefix(d.objet)}`, corps: quote });
  }

  async function openMessage(id: string, wasUnread: boolean) {
    setView('detail');
    setDetail(null);
    setLoading(true);
    setError(null);
    try {
      const m = await api.messagerie.getMessage(id);
      setDetail(m);
      // L'ouverture marque le message lu côté serveur : on reflète localement.
      if (wasUnread) {
        setInbox((prev) => prev.map((i) => (i.id === id ? { ...i, lu: true } : i)));
        setUnread((u) => Math.max(0, u - 1));
      }
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Message introuvable');
    } finally {
      setLoading(false);
    }
  }

  function toggleRecipient(id: string) {
    setRecipients((prev) =>
      prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id],
    );
  }

  /** Item 8.3 — tout le carnet est-il sélectionné ? */
  const tousSelectionnes = carnet.length > 0 && recipients.length === carnet.length;

  /** Item 8.3 — bascule : tout le carnet ↔ aucun. */
  function toggleTousRecipients() {
    setRecipients(tousSelectionnes ? [] : carnet.map((c) => c.id));
  }

  /**
   * @param brouillon `true` = enregistrer sans envoyer. Un brouillon n'exige
   * pas de destinataire : on écrit d'abord, on choisit à qui ensuite.
   */
  async function submitCompose(brouillon = false) {
    if (!objet.trim() || !corps.trim()) return;
    if (!brouillon && recipients.length === 0) return;
    setSending(true);
    setComposeError(null);
    try {
      if (brouillonId) {
        // On repart d'un brouillon existant : on le met à jour, puis on
        // l'envoie si demandé — plutôt que d'en créer un doublon.
        await api.messagerie.brouillons.maj(brouillonId, {
          objet: objet.trim(),
          corps: corps.trim(),
          destinataireIds: recipients,
          alerteId: alerteId || null,
        });
        if (!brouillon) await api.messagerie.brouillons.envoyer(brouillonId);
      } else {
        await api.messagerie.send({
          objet: objet.trim(),
          corps: corps.trim(),
          destinataireIds: recipients,
          alerteId: alerteId || null,
          brouillon,
        });
      }
      toast.success(brouillon ? 'Brouillon enregistré.' : 'Message envoyé.');
      setBrouillonId(null);
      if (brouillon) goBrouillons(); else goSent();
    } catch (err) {
      setComposeError(
        err instanceof ApiException ? err.payload.error : 'Échec de l\u2019envoi',
      );
    } finally {
      setSending(false);
    }
  }

  /** Reprendre un brouillon dans le composeur. */
  async function reprendreBrouillon(id: string) {
    try {
      const d = await api.messagerie.getMessage(id);
      await openCompose({ objet: d.objet, corps: d.corps });
      setBrouillonId(id);
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Brouillon illisible');
    }
  }

  const canSend = objet.trim() !== '' && corps.trim() !== '' && recipients.length > 0;
  const canSaveDraft = objet.trim() !== '' && corps.trim() !== '';

  return (
    // La messagerie est une brique à part entière sur le launcher. Elle
    // portait pourtant l'en-tête « GRC & communication » et les onglets du
    // GRC : un agent des espaces verts atterrissait dans une brique qui n'est
    // pas la sienne. En-tête neutre désormais.
    <>
      <AppHeader module="Messagerie interne" accent="grc" />
      <main className="mx-auto max-w-3xl px-4 py-5 space-y-4 animate-fade-in">
        {/* Onglets + nouveau message */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={goInbox}
            className={tabClass(view === 'inbox' || (view === 'detail' && !detail?.estExpediteur))}
          >
            <Inbox className="h-4 w-4" aria-hidden="true" />
            Reçus
            {unread > 0 && (
              <Badge variant="info" size="sm">{unread}</Badge>
            )}
          </button>
          <button
            type="button"
            onClick={goSent}
            className={tabClass(view === 'sent' && !archives)}
          >
            <Send className="h-4 w-4" aria-hidden="true" />
            Envoyés
          </button>
          <button
            type="button"
            onClick={goBrouillons}
            className={tabClass(view === 'brouillons')}
          >
            <FileText className="h-4 w-4" aria-hidden="true" />
            Brouillons
          </button>
          {/* Archives à part : c'est tout l'objet de l'item 8.5 — ne pas
              polluer l'affichage courant avec ce qui est rangé. */}
          <button
            type="button"
            onClick={goArchives}
            className={tabClass(archives)}
          >
            <Archive className="h-4 w-4" aria-hidden="true" />
            Archives
          </button>
          <button
            type="button"
            onClick={() => { setView('groupes'); setArchives(false); }}
            className={tabClass(view === 'groupes')}
          >
            <Users className="h-4 w-4" aria-hidden="true" />
            Groupes
          </button>
          <div className="ml-auto flex gap-2">
            {/* Vague B (mig 254) — une DIFFUSION n'est pas un message ordinaire :
                on décrit une cible au lieu de cocher des noms, et on attend une
                réponse de chacun. Deux boutons voisins, deux gestes distincts. */}
            {peutDiffuser && (
              <Button variant="glass" size="sm" onClick={() => navigate('/diffusions/nouvelle')}>
                <Megaphone className="h-4 w-4" aria-hidden="true" />
                Diffusion
              </Button>
            )}
            <Button variant="primary" size="sm" onClick={() => void openCompose()}>
              <Plus className="h-4 w-4" aria-hidden="true" />
              Nouveau message
            </Button>
          </div>
        </div>

        {/* Filtres et pagination — masqués sur le composeur et le détail */}
        {(view === 'inbox' || view === 'sent' || view === 'brouillons') && (
          <div className="flex flex-wrap items-center gap-2 text-xs">
            {view === 'inbox' && !archives && (
              <div className="flex items-center gap-1">
                {([
                  ['tous', 'Tous'],
                  ['en_cours', 'En cours'],
                  ['traite', 'Traités'],
                ] as const).map(([v, label]) => (
                  <button
                    key={v}
                    type="button"
                    aria-pressed={statutFiltre === v}
                    onClick={() => { setStatutFiltre(v); setOffset(0); }}
                    className={[
                      'rounded-r-md border px-2.5 py-1 transition-all',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
                      statutFiltre === v
                        ? 'border-grc/50 bg-grc/10 font-medium text-ink'
                        : 'border-border bg-surface/40 text-ink-muted hover:border-grc/40',
                    ].join(' ')}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}

            <div className="ml-auto flex items-center gap-2">
              <label htmlFor="msg-page-size" className="text-ink-subtle">
                Par page
              </label>
              <select
                id="msg-page-size"
                value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setOffset(0); }}
                className="h-8 rounded-r-md border border-border bg-surface/60 px-2 text-xs text-ink focus:border-grc focus:outline-none focus:ring-2 focus:ring-grc"
              >
                {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>

              <span className="tabular-nums text-ink-muted">
                {total === 0 ? '0' : `${offset + 1}–${Math.min(offset + pageSize, total)}`} sur {total}
              </span>

              <Button
                type="button" variant="ghost" size="sm"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - pageSize))}
              >
                Précédent
              </Button>
              <Button
                type="button" variant="ghost" size="sm"
                disabled={offset + pageSize >= total}
                onClick={() => setOffset(offset + pageSize)}
              >
                Suivant
              </Button>
            </div>
          </div>
        )}

        {error && <Alert variant="error" icon={AlertCircle}>{error}</Alert>}

        {loading && view !== 'compose' && view !== 'groupes' && (
          <Card variant="glass" padded>
            <div className="flex items-center gap-2 text-sm text-ink-muted">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Chargement…
            </div>
          </Card>
        )}

        {/* ===== Reçus ===== */}
        {!loading && view === 'inbox' && (
          inbox.length === 0 ? (
            <EmptyBox icon={Inbox} titre="Aucun message reçu"
              sub="Les messages de vos collègues apparaîtront ici." />
          ) : (
            <div className="space-y-2">
              {inbox.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => void openMessage(m.id, !m.lu)}
                  className="block w-full text-left"
                >
                  <Card variant="glass" padded className="transition-colors hover:border-grc/40">
                    <div className="flex items-start gap-2.5">
                      {!m.lu && (
                        <span className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-info"
                          aria-label="Non lu" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className={`truncate text-sm ${m.lu ? 'font-medium text-ink' : 'font-semibold text-ink'}`}>
                            {m.objet}
                          </span>
                          <span className="flex-shrink-0 text-2xs text-ink-subtle">
                            {relativeTime(m.createdAt)}
                          </span>
                        </div>
                        <p className="mt-0.5 text-xs text-ink-muted">
                          De {m.expediteurNom}
                        </p>
                        {m.alerte && (
                          <div className="mt-1"><AlerteBadge alerte={m.alerte} /></div>
                        )}
                        {m.traite && (
                          <div className="mt-1">
                            <Badge variant="success" size="sm">Traité</Badge>
                          </div>
                        )}

                        {/* Actions individuelles. `stopPropagation` : la ligne
                            entière ouvre le message, ces boutons non. */}
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); void basculerTraite(m.id, !m.traite); }}
                            className="inline-flex items-center gap-1 rounded-r-md border border-border bg-surface/40 px-2 py-0.5 text-2xs text-ink-muted transition-all hover:border-grc/40 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc"
                          >
                            <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
                            {m.traite ? 'Rouvrir' : 'Marquer traité'}
                          </button>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); void basculerArchive(m.id, !m.archive); }}
                            className="inline-flex items-center gap-1 rounded-r-md border border-border bg-surface/40 px-2 py-0.5 text-2xs text-ink-muted transition-all hover:border-grc/40 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc"
                          >
                            {m.archive
                              ? <><ArchiveRestore className="h-3 w-3" aria-hidden="true" /> Désarchiver</>
                              : <><Archive className="h-3 w-3" aria-hidden="true" /> Archiver</>}
                          </button>
                        </div>
                      </div>
                      <ChevronRight className="mt-0.5 h-4 w-4 flex-shrink-0 text-ink-subtle" aria-hidden="true" />
                    </div>
                  </Card>
                </button>
              ))}
            </div>
          )
        )}

        {/* ===== Envoyés ===== */}
        {!loading && view === 'sent' && (
          sent.length === 0 ? (
            <EmptyBox icon={Send} titre="Aucun message envoyé"
              sub="Vos messages envoyés apparaîtront ici." />
          ) : (
            <div className="space-y-2">
              {sent.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => void openMessage(m.id, false)}
                  className="block w-full text-left"
                >
                  <Card variant="glass" padded className="transition-colors hover:border-grc/40">
                    <div className="flex items-start gap-2.5">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate text-sm font-medium text-ink">{m.objet}</span>
                          <span className="flex-shrink-0 text-2xs text-ink-subtle">
                            {relativeTime(m.createdAt)}
                          </span>
                        </div>
                        <p className="mt-0.5 text-xs text-ink-muted line-clamp-1">
                          À {m.destinataires}
                        </p>
                        {m.alerte && (
                          <div className="mt-1"><AlerteBadge alerte={m.alerte} /></div>
                        )}

                        {/* « Où ça en est » : 2/3, pas un booléen — trois
                            services avancent à trois vitesses. */}
                        {/* Mig 254 — une diffusion se suit à l'acquittement, pas
                            au drapeau « traité ». Le lien évite d'avoir à
                            connaître l'URL du suivi. */}
                        {m.retourAttendu && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); navigate(`/diffusions/${m.id}/suivi`); }}
                            className="inline-flex items-center gap-1 text-xs font-medium text-grc underline underline-offset-2"
                          >
                            <Megaphone className="h-3.5 w-3.5" aria-hidden="true" />
                            Diffusion — {m.acquittesCount ?? 0}/{m.destinatairesCount} ont répondu
                          </button>
                        )}
                        {!m.retourAttendu && m.destinatairesCount > 0 && (
                          <div className="mt-1">
                            <Badge
                              variant={m.traitesCount === m.destinatairesCount ? 'success' : 'neutral'}
                              size="sm"
                            >
                              {m.traitesCount}/{m.destinatairesCount} traité
                              {m.destinatairesCount > 1 ? 's' : ''}
                            </Badge>
                          </div>
                        )}

                        <div className="mt-2">
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); void basculerArchive(m.id, !m.archive); }}
                            className="inline-flex items-center gap-1 rounded-r-md border border-border bg-surface/40 px-2 py-0.5 text-2xs text-ink-muted transition-all hover:border-grc/40 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-grc"
                          >
                            {m.archive
                              ? <><ArchiveRestore className="h-3 w-3" aria-hidden="true" /> Désarchiver</>
                              : <><Archive className="h-3 w-3" aria-hidden="true" /> Archiver</>}
                          </button>
                        </div>
                      </div>
                      <ChevronRight className="mt-0.5 h-4 w-4 flex-shrink-0 text-ink-subtle" aria-hidden="true" />
                    </div>
                  </Card>
                </button>
              ))}
            </div>
          )
        )}

        {/* ===== Brouillons ===== */}
        {!loading && view === 'brouillons' && (
          sent.length === 0 ? (
            <EmptyBox icon={FileText} titre="Aucun brouillon"
              sub="Un message enregistré sans être envoyé se retrouve ici." />
          ) : (
            <div className="space-y-2">
              {sent.map((m) => (
                <Card key={m.id} variant="glass" padded>
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-ink">{m.objet}</p>
                      <p className="mt-0.5 text-xs text-ink-muted line-clamp-1">
                        {m.destinatairesCount === 0
                          ? 'Aucun destinataire choisi'
                          : `À ${m.destinataires}`}
                      </p>
                      <p className="mt-0.5 text-2xs text-ink-subtle">
                        Écrit {relativeTime(m.createdAt)}
                      </p>
                    </div>
                    <div className="flex flex-shrink-0 items-center gap-1.5">
                      <Button
                        type="button" variant="secondary" size="sm"
                        onClick={() => void reprendreBrouillon(m.id)}
                      >
                        Reprendre
                      </Button>
                      <Button
                        type="button" variant="ghost" size="sm"
                        onClick={() => void supprimerBrouillon(m.id)}
                      >
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    </div>
                  </div>
                  {m.destinatairesCount === 0 && (
                    <p className="mt-2 text-2xs text-ink-muted">
                      Il faudra choisir au moins un destinataire pour l'envoyer.
                    </p>
                  )}
                </Card>
              ))}
            </div>
          )
        )}

        {/* ===== Groupes (item 8.4) ===== */}
        {view === 'groupes' && <MessagerieGroupesPanel />}

        {/* ===== Détail ===== */}
        {!loading && view === 'detail' && detail && (
          <Card variant="glass" padded>
            <h2 className="font-display text-lg font-semibold tracking-r-snug text-ink">
              {detail.objet}
            </h2>
            {detail.alerte && (
              <div className="mt-1.5">
                <AlerteBadge alerte={detail.alerte} />
              </div>
            )}
            <div className="mt-2 space-y-0.5 text-xs text-ink-muted">
              <p>
                <span className="text-ink-subtle">De </span>
                {detail.expediteurNom}{' '}
                <span className="text-ink-subtle">· {detail.expediteurEmail}</span>
              </p>
              <p>
                <span className="text-ink-subtle">À </span>
                {detail.destinataires.map((d) => d.nomComplet).join(', ')}
              </p>
              <p className="text-ink-subtle">
                {new Date(detail.createdAt).toLocaleString('fr-FR')}
              </p>
            </div>
            <div className="mt-4 whitespace-pre-wrap border-t border-border pt-4 text-sm text-ink">
              {detail.corps}
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              <Button variant="secondary" size="sm" onClick={detail.estExpediteur ? goSent : goInbox}>
                <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                Retour
              </Button>
              {!detail.estExpediteur && (
                <Button variant="primary" size="sm" onClick={() => replyTo(detail)}>
                  <Reply className="h-4 w-4" aria-hidden="true" />
                  Répondre
                </Button>
              )}
              <Button variant="secondary" size="sm" onClick={() => forwardMsg(detail)}>
                <Forward className="h-4 w-4" aria-hidden="true" />
                Transférer
              </Button>
            </div>
          </Card>
        )}

        {/* ===== Composer ===== */}
        {view === 'compose' && (
          <Card variant="glass" padded>
            <h2 className="font-display text-base font-semibold tracking-r-snug text-ink">
              Nouveau message
            </h2>

            {composeError && (
              <Alert variant="error" icon={AlertCircle} className="mt-3">{composeError}</Alert>
            )}

            {/* Carnet d'adresses */}
            <div className="mt-4">
              <div className="flex items-center justify-between gap-2">
                <label className="flex items-center gap-1.5 text-2xs uppercase tracking-r-eyebrow text-ink-subtle">
                  <Users className="h-3.5 w-3.5" aria-hidden="true" />
                  Destinataires{recipients.length > 0 && ` · ${recipients.length} sélectionné${recipients.length > 1 ? 's' : ''}`}
                </label>
                {/* Item 8.3 du retour mairie : « bouton Tous dans la messagerie
                    interne, sans cocher chacun ».
                    Bascule tout/rien plutôt que « tout cocher » seul : une fois
                    tout coché, le geste naturel est de tout décocher — sinon il
                    faut re-cliquer sur chaque nom, ce que l'item veut justement
                    éviter. */}
                {carnet.length > 0 && (
                  <button
                    type="button"
                    onClick={toggleTousRecipients}
                    className="rounded-r-md border border-border bg-surface/60 px-2 py-1 text-2xs text-ink transition-colors hover:bg-surface hover:border-border-hi focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[oklch(var(--c-grc))]"
                    aria-pressed={tousSelectionnes}
                  >
                    {tousSelectionnes ? 'Aucun' : `Tous (${carnet.length})`}
                  </button>
                )}
              </div>
              <div className="mt-1.5 max-h-56 overflow-y-auto rounded-r-lg border border-border bg-bg-2/40">
                {carnet.length === 0 ? (
                  <p className="px-3 py-4 text-xs text-ink-subtle">
                    Aucun collègue dans le carnet d'adresses.
                  </p>
                ) : (
                  carnet.map((c) => {
                    const selected = recipients.includes(c.id);
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => toggleRecipient(c.id)}
                        className={`flex w-full items-center gap-2.5 border-b border-border/60 px-3 py-2 text-left transition-colors last:border-b-0 ${
                          selected ? 'bg-grc-tint/50' : 'hover:bg-surface'
                        }`}
                      >
                        <span
                          className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-r-sm border ${
                            selected
                              ? 'border-grc bg-grc text-white'
                              : 'border-border bg-surface'
                          }`}
                          aria-hidden="true"
                        >
                          {selected && <Check className="h-3.5 w-3.5" />}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm text-ink">{c.nomComplet}</span>
                          <span className="block truncate text-2xs text-ink-subtle">
                            {ROLE_LABEL[c.role] ?? c.role} · {c.email}
                          </span>
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            {/* Objet */}
            <div className="mt-4">
              <label htmlFor="msg-objet" className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle">
                Objet
              </label>
              <input
                id="msg-objet"
                type="text"
                value={objet}
                onChange={(e) => setObjet(e.target.value)}
                maxLength={200}
                placeholder="Ex : Intervention prévue rue des Écoles"
                className="mt-1 h-10 w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass px-2.5 text-sm text-ink shadow-soft transition-all focus:border-grc focus:outline-none focus:ring-2 focus:ring-grc focus:ring-offset-2 focus:ring-offset-bg"
              />
            </div>

            {/* Corps */}
            <div className="mt-3">
              <label htmlFor="msg-corps" className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle">
                Message
              </label>
              <textarea
                id="msg-corps"
                value={corps}
                onChange={(e) => setCorps(e.target.value)}
                rows={6}
                maxLength={5000}
                placeholder="Votre message…"
                className="mt-1 w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass p-2.5 text-sm text-ink shadow-soft transition-all focus:border-grc focus:outline-none focus:ring-2 focus:ring-grc focus:ring-offset-2 focus:ring-offset-bg"
              />
            </div>

            {/* Rattacher à une alerte / crise (optionnel) — mini-lot alerte T1 */}
            {alertesRattachables.length > 0 && (
              <div className="mt-3">
                <label htmlFor="msg-alerte" className="text-2xs uppercase tracking-r-eyebrow text-ink-subtle">
                  Rattacher à une alerte / crise (optionnel)
                </label>
                <select
                  id="msg-alerte"
                  value={alerteId}
                  onChange={(e) => setAlerteId(e.target.value)}
                  className="mt-1 h-10 w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass px-2.5 text-sm text-ink shadow-soft transition-all focus:border-grc focus:outline-none focus:ring-2 focus:ring-grc focus:ring-offset-2 focus:ring-offset-bg"
                >
                  <option value="">— Aucune —</option>
                  {alertesRattachables.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.niveau === 'crise' ? '🔴 Crise' : '🟠 Alerte'} · {a.titre}
                    </option>
                  ))}
                </select>
                {alerteId && (
                  <p className="mt-1 text-2xs text-ink-muted">
                    Ce message sera rattaché à cette alerte et apparaîtra dans son journal de crise.
                  </p>
                )}
              </div>
            )}

            <div className="mt-5 flex flex-wrap gap-2 justify-end">
              <Button variant="secondary" size="sm" onClick={goInbox} disabled={sending}>
                Annuler
              </Button>
              {/* Enregistrer sans destinataire est permis : on écrit d'abord,
                  on choisit à qui ensuite. */}
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void submitCompose(true)}
                loading={sending}
                disabled={!canSaveDraft}
              >
                <FileText className="h-4 w-4" aria-hidden="true" />
                Enregistrer comme brouillon
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => void submitCompose(false)}
                loading={sending}
                disabled={!canSend}
              >
                <Send className="h-4 w-4" aria-hidden="true" />
                Envoyer
              </Button>
            </div>
          </Card>
        )}
      </main>
    </>
  );
}

// ---------- Sous-composants ----------

function tabClass(active: boolean): string {
  return [
    'inline-flex h-9 items-center gap-1.5 rounded-r-pill border px-3 text-xs font-medium transition-all',
    active
      ? 'border-grc/30 bg-grc-tint/80 text-grc-fg'
      : 'border-border bg-surface/60 text-ink-muted hover:bg-surface/80 hover:text-ink',
  ].join(' ');
}

function EmptyBox({
  icon: Icon,
  titre,
  sub,
}: {
  icon: typeof Inbox;
  titre: string;
  sub: string;
}) {
  return (
    <Card variant="glass" padded>
      <div className="py-8 text-center">
        <Icon className="mx-auto h-10 w-10 text-ink-dim" aria-hidden="true" />
        <h2 className="mt-3 font-display text-base font-semibold text-ink">{titre}</h2>
        <p className="mt-1 text-sm text-ink-muted">{sub}</p>
      </div>
    </Card>
  );
}
